import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "../../services/supabaseClient";
import PrimaryButton from "../Button/PrimaryButton";
import Logo from "../../assets/LOGO.png";
import { Box, Typography, CircularProgress } from "@mui/material";
import { toast } from 'react-toastify';

/**
 * Password Reset Confirmation Page
 * Prevents auto-consumption of magic links by requiring user interaction
 */
const PasswordResetConfirmation = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [isProcessing, setIsProcessing] = useState(false);
  const [confirmationUrl, setConfirmationUrl] = useState('');
  const [isValid, setIsValid] = useState(true);

  useEffect(() => {
    // Extract confirmation URL from query parameters
    const params = new URLSearchParams(location.search);
    const url = params.get('confirmation_url');
    
    if (!url) {
      setIsValid(false);
      toast.error(t('invalid_reset_link') || 'Invalid password reset link');
      setTimeout(() => navigate('/'), 3000);
    } else {
      setConfirmationUrl(decodeURIComponent(url));
    }
  }, [location, navigate, t]);

  const handleConfirmReset = async () => {
    if (!confirmationUrl || isProcessing) return;
    
    setIsProcessing(true);
    
    try {
      // Navigate to the actual confirmation URL
      // This triggers the Supabase magic link consumption
      window.location.href = confirmationUrl;
    } catch (error) {
      console.error("Error processing reset link:", error);
      toast.error(t('reset_link_error') || 'Error processing reset link');
      setIsProcessing(false);
    }
  };

  const handleCancel = () => {
    navigate('/');
  };

  if (!isValid) {
    return (
      <div className="prisijungimoLangas">
        <div className="lentele">
          <div className="lenteleVidus" style={{ textAlign: 'center' }}>
            <Typography variant="h5" color="error" gutterBottom>
              {t('invalid_link')}
            </Typography>
            <Typography variant="body1" gutterBottom>
              {t('redirecting')}...
            </Typography>
            <CircularProgress />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="prisijungimoLangas">
      <Box sx={{ p: 4, display: { xs: "block", sm: "none"} }}>
        <Typography
          sx={{
            textAlign: "center",
            py: 2,
            color: "common.white",
            fontWeight: 700,
            fontSize: "1.5em" 
          }}
        >
          {t('confirm_password_reset')}
        </Typography>
      </Box>

      <div className="lentele">
        <div className="lenteleVidus">
          <Box sx={{ display: { xs: "none", sm: "block"} }}>
            <Typography
              sx={{
                textAlign: "center",
                py: 2,
                color: "primary.main",
                fontWeight: 700,
                fontSize: "1.5em" 
              }}
            >
              {t('confirm_password_reset')}
            </Typography>
          </Box>
          
          <Box sx={{ my: 3, textAlign: 'center' }}>
            <Typography variant="body1" gutterBottom>
              {t('click_below_to_reset_password') || 'Click the button below to proceed with resetting your password.'}
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
              {t('link_expires_warning') || 'This link will expire after use or after 1 hour.'}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 4 }}>
            <PrimaryButton
              text={isProcessing ? 'processing' : 'confirm_reset'}
              onClick={handleConfirmReset}
              disabled={isProcessing}
              className="mobLogIn"
            />
            <PrimaryButton
              text="cancel"
              onClick={handleCancel}
              disabled={isProcessing}
              className="mobLogIn secondary"
            />
          </Box>

          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Typography variant="caption" color="textSecondary">
              {t('security_notice') || 'This extra step helps protect your account from unauthorized access.'}
            </Typography>
          </Box>
        </div>
      </div>
      
      <a href="http://www.ivairove.lt" target="_blank" className="loginLogoCont" rel="noreferrer">
        <img className="loginLogo" src={Logo} alt="logo" />
      </a>
    </div>
  );
};

export default PasswordResetConfirmation;